// src/components/ContactForm.js
import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './ContactForm.css'; // Your existing CSS

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';
// Ensure this is your V2 Checkbox Site Key
const RECAPTCHA_SITE_KEY = '6Le1HjcrAAAAAM1ssJZRB9ZSgfhx9UgwkZ3BqERO'; // Use your actual site key

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '', 
    email: '',
    subject: '', 
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const recaptchaRef = useRef(null); // Still useful if you need to access other methods, but not for reset
  const submittingRef = useRef(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  // State to force re-render of ReCAPTCHA component by changing its key
  const [recaptchaRenderKey, setRecaptchaRenderKey] = useState(Date.now());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(''); 
    setSuccess(false); 
  };

  const handleRecaptchaChange = (token) => {
    console.log("ContactForm reCAPTCHA token obtained:", token);
    setRecaptchaToken(token);
    setError(''); 
  };

  const handleRecaptchaError = () => {
    setError('reCAPTCHA challenge failed. Please try again.');
    setRecaptchaToken(null);
    // Force re-render on error too, to get a fresh widget
    setRecaptchaRenderKey(Date.now()); 
  };
  
  const handleRecaptchaExpire = () => {
    console.log("ContactForm reCAPTCHA token expired");
    setRecaptchaToken(null);
    // Force re-render on expire to allow user to try again easily
    setRecaptchaRenderKey(Date.now());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingRef.current) {
      return;
    }

    setError('');
    setSuccess(false);

    if (RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY !== 'YOUR_RECAPTCHA_SITE_KEY_HERE' && !recaptchaToken) {
      setError('Please complete the reCAPTCHA verification.');
      return;
    }

    if (!formData.email || !formData.message) {
        setError('Email and Message are required.');
        return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    const submissionPayload = {
      // Use capitalized keys to match your Strapi API IDs
      Email: formData.email,
      Message: formData.message,
    };
    if (formData.name && formData.name.trim() !== '') {
        submissionPayload.Name = formData.name;
    }
    if (formData.subject && formData.subject.trim() !== '') {
        submissionPayload.Subject = formData.subject;
    }

    console.log("Payload being sent to Strapi:", { data: submissionPayload });

    try {
      const response = await fetch(`${STRAPI_URL}/api/contact-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
            data: submissionPayload 
        }),
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        const message = responseData.error?.message || `An error occurred: ${response.statusText} (Status: ${response.status})`;
        if (responseData.error?.details) {
            console.error("Strapi validation error details:", responseData.error.details);
            if (Array.isArray(responseData.error.details.errors)) {
                responseData.error.details.errors.forEach(err => {
                    console.error(`Field: ${err.path?.join('.')}, Message: ${err.message}`);
                });
            }
        }
        throw new Error(message);
      }
      
      setSuccess(true);
      // Reset form using lowercase keys that match the 'name' attributes of inputs
      setFormData({ name: '', email: '', subject: '', message: '' }); 
      
      // Reset reCAPTCHA by changing its key, forcing a re-mount
      setRecaptchaRenderKey(Date.now());
      
      setRecaptchaToken(null); // Clear local token state

    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'Failed to send message. Please try again.');
      // Also reset reCAPTCHA on error by changing key
      setRecaptchaRenderKey(Date.now());
      setRecaptchaToken(null);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (!RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY === 'YOUR_RECAPTCHA_SITE_KEY_HERE') {
    console.warn("reCAPTCHA Site Key is NOT CONFIGURED in .env (REACT_APP_RECAPTCHA_SITE_KEY). reCAPTCHA will not be displayed.");
  }

  return (
    <section className="contactFormSection">
      {success ? (
        <p className="thankYouMessage">
          Thank you for your message. We'll be in touch soon!
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="contactForm">
          <div className="formGroup">
            <label htmlFor="name">Name</label>
            <input id="name" type="text" name="name" value={formData.name} onChange={handleChange} disabled={isSubmitting}/>
          </div>
          <div className="formGroup">
            <label htmlFor="email">Email *</label>
            <input id="email" type="email" name="email" value={formData.email} onChange={handleChange} required disabled={isSubmitting}/>
          </div>
          <div className="formGroup">
            <label htmlFor="subject">Subject</label>
            <input id="subject" type="text" name="subject" value={formData.subject} onChange={handleChange} disabled={isSubmitting}/>
          </div>
          <div className="formGroup">
            <label htmlFor="message">Message *</label>
            <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="5" required disabled={isSubmitting}></textarea>
          </div>
          
          {RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY !== 'YOUR_RECAPTCHA_SITE_KEY_HERE' && (
            <div className="formGroup recaptcha-wrapper">
              <ReCAPTCHA
                key={recaptchaRenderKey} /* Pass the key here */
                ref={recaptchaRef} /* Keep ref if other imperative methods are ever needed */
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                onErrored={handleRecaptchaError}
                onExpired={handleRecaptchaExpire}
              />
            </div>
          )}
          {error && <p className="errorMessage">{error}</p>}
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Submit Message'}
          </button>
        </form>
      )}
    </section>
  );
};

export default ContactForm;
